import { useFormikContext } from "formik"
import { MenuItem, Autocomplete, Stack, TextField, Typography } from "@mui/material"
import { FormikDropdown, FormikSubmitButton, FormikTextField } from "../../../../shared/components/forms/Formik"
import { MAX_NAME_LENGTH } from "../../../../constants/inputValidationDefaults"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { TransformedCompany } from "../../../login/loginTypes"
import { SelfSignUpForm } from "../pages/selfSignUpTypes"

type SelfSignUpFormContentCompanyProps = Readonly<{
    companies: TransformedCompany[]
}>

export const SelfSignUpFormContentCompany = ({ companies }: SelfSignUpFormContentCompanyProps) => {
    const { values, setFieldValue } = useFormikContext<SelfSignUpForm>()
    const selectedCompany = companies.find((company) => company.id === values.companyId)

    const signUpSettings = selectedCompany
        ? selectedCompany.isGroup
            ? selectedCompany.signUpSettings
            : (
                  selectedCompany.userGroups?.find((group) => group.id === Number(values.userGroupId)) ??
                  selectedCompany.userGroup
              )?.signUpSettings
        : undefined

    const emailDomainMessage = signUpSettings?.emailDomainMessage
    const acceptedEmailDomains = signUpSettings?.acceptedEmailDomains

    const handleCompanyChange = (newCompany: TransformedCompany | null) => {
        const newUserGroupId = newCompany
            ? newCompany.isGroup
                ? String(newCompany.userGroupId)
                : newCompany?.userGroups?.length === 1
                  ? String(newCompany.userGroups[0].id)
                  : null
            : null

        setFieldValue("companyId", newCompany?.id)
        setFieldValue("userGroupId", newUserGroupId)
        setFieldValue("email", "")
        setFieldValue("name", "")
        setFieldValue("mobileNumber", "")
    }

    return (
        <>
            {companies.length > 1 ? (
                <LocalizedStrict id="self-sign-up-field-company" attrs={{ label: true, placeholder: true }}>
                    <Autocomplete
                        fullWidth
                        options={companies}
                        value={selectedCompany || null}
                        onChange={(_, newValue) => handleCompanyChange(newValue)}
                        getOptionLabel={(option) => option.displayName}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Your company:"
                                placeholder="Select the company you work for"
                            />
                        )}
                    />
                </LocalizedStrict>
            ) : (
                companies.length === 1 && (
                    <Typography>
                        <LocalizedStrict id="id-card-signup-using-company">
                            <>Your company:</>
                        </LocalizedStrict>{" "}
                        <Typography component="span" fontWeight="bold">
                            {companies[0].displayName}
                        </Typography>
                    </Typography>
                )
            )}

            {selectedCompany && !selectedCompany?.isGroup && selectedCompany.userGroups.length > 1 ? (
                <LocalizedStrict id="self-sign-up-field-user-group" attrs={{ label: true, placeholder: true }}>
                    <FormikDropdown<SelfSignUpForm>
                        fullWidth
                        name="userGroupId"
                        label="Your user group:"
                        placeholder="Select user group"
                    >
                        {!selectedCompany?.isGroup &&
                            selectedCompany?.userGroups.map((group) => (
                                <MenuItem key={group.id} value={String(group.id)}>
                                    {group.name}
                                </MenuItem>
                            ))}
                    </FormikDropdown>
                </LocalizedStrict>
            ) : (
                selectedCompany &&
                !selectedCompany.isGroup &&
                selectedCompany.userGroups?.length === 1 && (
                    <Typography>
                        <LocalizedStrict id="id-card-signup-using-user-group">
                            <>Your user group:</>
                        </LocalizedStrict>{" "}
                        <Typography component="span" fontWeight="bold">
                            {" "}
                            {selectedCompany.userGroups[0].name}
                        </Typography>
                    </Typography>
                )
            )}

            {values.companyId && values.userGroupId && (
                <>
                    <LocalizedStrict id="self-sign-up-field-name" attrs={{ label: true, placeholder: true }}>
                        <FormikTextField<SelfSignUpForm>
                            name="name"
                            label="Your name (*):"
                            placeholder="Enter your full name"
                            fullWidth
                            inputProps={{ maxLength: MAX_NAME_LENGTH }}
                        />
                    </LocalizedStrict>
                    <Stack gap="0.25rem">
                        {emailDomainMessage ? (
                            <Typography variant="caption" color="text.secondary">
                                {emailDomainMessage}
                            </Typography>
                        ) : (
                            !!acceptedEmailDomains?.length && (
                                <LocalizedStrict
                                    id="self-sign-up-email-domain-hint"
                                    vars={{ domains: acceptedEmailDomains.join(", ") }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        Please use an email from an approved domain: {acceptedEmailDomains.join(", ")}
                                    </Typography>
                                </LocalizedStrict>
                            )
                        )}
                        <LocalizedStrict id="self-sign-up-field-email" attrs={{ label: true, placeholder: true }}>
                            <FormikTextField<SelfSignUpForm>
                                name="email"
                                label="Email:"
                                placeholder="Enter your email"
                                fullWidth
                            />
                        </LocalizedStrict>
                    </Stack>
                    <LocalizedStrict id="self-sign-up-field-mobile-number" attrs={{ label: true, placeholder: true }}>
                        <FormikTextField<SelfSignUpForm>
                            name="mobileNumber"
                            label="Your phone:"
                            placeholder="Enter your mobile number (optional)"
                            autoComplete="mobile"
                            fullWidth
                        />
                    </LocalizedStrict>
                    <LocalizedStrict id="self-sign-up-complete-button">
                        <FormikSubmitButton fullWidth variant="contained" type="submit">
                            Sign Up
                        </FormikSubmitButton>
                    </LocalizedStrict>
                </>
            )}
        </>
    )
}
